import React, { useEffect, useRef, useState } from 'react'
import Icon from '../Icon2/Icon'
import Paragraph from '../Typography/Paragraph/Paragraph'
import Checkbox from '../Checkbox/Checkbox'
import './TreeNavigator.css'

export interface TreeNavigatorData {
  id: string
  title: string
  icon?: React.ReactNode | string
  expandedIcon?: React.ReactNode | string
  children?: TreeNavigatorData[]
  loading?: boolean
  hasChildren?: boolean
  checked?: boolean
  indeterminate?: boolean
}

type IconSize = "extraSmall" | "large" | "medium" | "mini" | "original" | "small" | "tiny"
type VersionType = "v1" | "v2"

interface TreeNavigatorProps {
  data: TreeNavigatorData[]
  collapsible: boolean
  collapseOnSelectItem?: boolean
  selectOnChevronClick?: boolean
  hoverable?: boolean
  hoverScale?: boolean
  hoverItem?: boolean
  checkbox?: boolean
  checkboxFocus?: boolean
  checkboxLinked?: boolean
  collapsedIds: string[]
  selectedItem: string | null
  truncation?: boolean
  titleVariant?: 'p1' | 'p2' | 'p3'
  nodePadding?: number | null
  childSpacing?: number | null
  hierarchicalSpacing?: boolean
  stepSpacing?: number
  translateOnExpansion?: boolean
  chevronVersion?: VersionType
  chevronIcon?: string
  chevronSize?: IconSize
  chevronClassname?: string
  prefixIconSize?: IconSize
  prefixIconClassName?: string
  prefixExpandedIconClassName?: string
  showBullets?: boolean
  bulletClassname?: string
  onSelect: (result: { id: string, title: string }) => void
  onCollapse: (id: string) => void
  onDataChange?: (data: TreeNavigatorData[]) => void
  onExpand?: (nodeId: string) => Promise<TreeNavigatorData[]> | TreeNavigatorData[]
}

/** Function declaration (not const) so the binding exists under circular imports via src/index. */
function TreeNavigator({
  data,
  collapsible,
  collapseOnSelectItem = false,
  selectOnChevronClick = false,
  hoverable = true,
  hoverScale = true,
  hoverItem = false,
  checkbox = false,
  checkboxFocus = false,
  checkboxLinked = true,
  collapsedIds,
  selectedItem,
  truncation = true,
  titleVariant = 'p2',
  nodePadding = 8,
  childSpacing = 16,
  hierarchicalSpacing = false,
  stepSpacing = 32,
  translateOnExpansion = false,
  chevronVersion = 'v2',
  chevronIcon = 'Chevron',
  chevronSize = 'original',
  chevronClassname,
  prefixIconSize = 'original',
  prefixIconClassName,
  prefixExpandedIconClassName,
  showBullets = false,
  bulletClassname,
  onSelect,
  onCollapse,
  onDataChange,
  onExpand
}: TreeNavigatorProps) {
  const checkboxRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (checkboxRefs.current && checkbox) {
      data.forEach((item) => {
        if (checkboxRefs.current[item.id]) {
          checkboxRefs.current[item.id].indeterminate = item.indeterminate || false;
        }
      });
    }
  }, [data, checkbox]);

  const isCollapsed = (item: TreeNavigatorData) => {
    return collapsedIds.includes(item.id);
  }

  const updateNodeInTree = (
    nodes: TreeNavigatorData[],
    nodeId: string,
    updateFn: (node: TreeNavigatorData) => TreeNavigatorData
  ): TreeNavigatorData[] => {
    return nodes.map(node => {
      if (node.id === nodeId) {
        return updateFn(node);
      }
      if (node.children) {
        return {
          ...node,
          children: updateNodeInTree(node.children, nodeId, updateFn)
        };
      }
      return node;
    });
  };

  const updateNodeRecursively = (
    node: TreeNavigatorData,
    checked: boolean
  ): TreeNavigatorData => {
    return {
      ...node,
      checked,
      indeterminate: false,
      children: node.children?.map(child => updateNodeRecursively(child, checked))
    };
  };

  const updateParentStates = (nodes: TreeNavigatorData[]): TreeNavigatorData[] => {
    return nodes.map(node => {
      if (!node.children || node.children.length === 0) {
        return node;
      }

      const updatedChildren = updateParentStates(node.children);
      const allChecked = updatedChildren.every(child => child.checked && !child.indeterminate);
      const allUnchecked = updatedChildren.every(child => !child.checked && !child.indeterminate);

      return {
        ...node,
        children: updatedChildren,
        checked: allChecked,
        indeterminate: !(allChecked || allUnchecked)
      };
    });
  };

  const handleCheckboxClick = (item: TreeNavigatorData) => {
    const checked = !item.checked;

    if (checkboxLinked) {
      let updatedData = updateNodeInTree(
        data,
        item.id,
        (node) => updateNodeRecursively(node, checked)
      );

      updatedData = updateParentStates(updatedData);
      onDataChange?.(updatedData);
    } else {
      const updatedData = updateNodeInTree(
        data,
        item.id,
        (node) => ({ ...node, checked, indeterminate: false })
      );

      onDataChange?.(updatedData);
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      action();
    }
  }

  const handleChevronAction = async (item: TreeNavigatorData) => {
    const wasCollapsed = isCollapsed(item);

    onCollapse(item.id);
    if (selectOnChevronClick) {
      onSelect(item);
    }

    if (wasCollapsed && onExpand && (item.hasChildren || (!item.children && !item.loading))) {
      setLoadingNodes(prev => new Set(prev).add(item.id));

      try {
        await onExpand(item.id);
      } catch (error) {
        console.error('Error loading children:', error);
      } finally {
        setLoadingNodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(item.id);
          return newSet;
        });
      }
    }
  }

  const handleItemSelect = (item: TreeNavigatorData) => {
    if (collapseOnSelectItem) {
      onCollapse(item.id);
    }
    onSelect(item);
  }

  return (
    <>
      {data.map((item) => {
        const shouldApplyMargin = !item.children && !item.hasChildren && hierarchicalSpacing;
        const marginValue = shouldApplyMargin
          ? !checkbox ? `${stepSpacing}px` : `${stepSpacing + 10}px`
          : undefined;

        const containerClasses = [
          'TreeNavigator__item-container',
          translateOnExpansion
            ? (!isCollapsed(item) ? 'expanded' : 'collapsed')
            : ''
        ].filter(Boolean).join(' ');

        const itemClasses = [
          'TreeNavigator__item',
          (!item.children && !item.hasChildren && !hierarchicalSpacing) ? 'TreeNavigator__item-child' : '',
          selectedItem === item.id ? 'selected' : '',
          hoverable ? 'hoverable' : '',
          (hoverable && hoverScale) ? 'hover-scale' : '',
          hoverItem ? 'hover-item' : ''
        ].filter(Boolean).join(' ');

        const itemStyle = {
          padding: nodePadding !== null ? `${nodePadding}px` : '8px',
          marginLeft: marginValue
        };

        return (
          <div className={containerClasses} key={item.id}>
            <div className={itemClasses} style={itemStyle}>
              {(collapsible && ((item.children && item.children.length > 0) || item.hasChildren)) && (
                <div
                  className="TreeNavigator__focusable-element"
                  tabIndex={0}
                  role="button"
                  aria-label={`${isCollapsed(item) ? 'Expand' : 'Collapse'} ${item.title}`}
                  aria-expanded={!isCollapsed(item)}
                  onClick={() => handleChevronAction(item)}
                  onKeyDown={(event) => handleKeyDown(event, () => handleChevronAction(item))}
                  style={{ display: 'inline-flex', cursor: 'pointer' }}
                >
                  <Icon
                    version={chevronVersion}
                    icon={chevronIcon}
                    size={typeof chevronSize === 'string' ? chevronSize : 'original'}
                    className={`TreeNavigator__chevron ${isCollapsed(item) ? 'collapsed' : ''} ${chevronClassname || ''}`}
                  />
                </div>
              )}
              {!checkbox ? (
                <div
                  className="TreeNavigator__item-content TreeNavigator__focusable-element"
                  tabIndex={0}
                  role="button"
                  aria-label={`Select ${item.title}`}
                  aria-selected={selectedItem === item.id}
                  onClick={() => handleItemSelect(item)}
                  onKeyDown={(event) => handleKeyDown(event, () => handleItemSelect(item))}
                >
                    {(showBullets && (!item.children || item.children.length === 0) && (
                        <div className={`TreeNavigator__bullet-container ${bulletClassname || ''}`}>
                            <div className='TreeNavigator__bullet'></div>
                        </div>
                    ))}
                    <PrefixIcon
                        item={item}
                        isCollapsed={isCollapsed}
                        prefixIconSize={prefixIconSize}
                        prefixIconClassName={prefixIconClassName}
                        prefixExpandedIconClassName={prefixExpandedIconClassName}
                    />
                  <Paragraph
                    text={item.title}
                    variant={titleVariant}
                    variantStyle="regular"
                    className={`TreeNavigator__item-title ${truncation ? 'truncation' : ''
                      } ${!item.children && !item.hasChildren ? 'TreeNavigator__item-title__bold' : ''
                      }`}
                  />
                </div>
              ) : (
                <div className="TreeNavigator__item-checkbox__content">
                  {(showBullets && (!item.children || item.children.length === 0) && (
                    <div className={`TreeNavigator__bullet-container ${bulletClassname || ''}`}>
                        <div className='TreeNavigator__bullet'></div>
                    </div>
                  ))}
                  <Checkbox
                    className={`TreeNavigator__item-checkbox ${checkboxFocus ? 'focus-visible' : 'focus-invisible'
                      }`}
                    inputRef={(ref) => {
                      if (ref) {
                        checkboxRefs.current[item.id] = ref;
                      }
                    }}
                    checked={item.checked || false}
                    onChange={() => handleCheckboxClick(item)}
                    aria-label={`${item.checked ? 'Uncheck' : 'Check'} ${item.title}`}
                  />
                  <PrefixIcon
                    item={item}
                    isCollapsed={isCollapsed}
                    prefixIconSize={prefixIconSize}
                    prefixIconClassName={prefixIconClassName}
                    prefixExpandedIconClassName={prefixExpandedIconClassName}
                  />
                  <div
                    className="TreeNavigator__focusable-element"
                    tabIndex={0}
                    role="button"
                    aria-label={`${item.checked ? 'Uncheck' : 'Check'} ${item.title}`}
                    onClick={() => handleCheckboxClick(item)}
                    onKeyDown={(event) => handleKeyDown(event, () => handleCheckboxClick(item))}
                    style={{ cursor: 'pointer' }}
                  >
                    <Paragraph
                      text={item.title}
                      variant={titleVariant}
                      variantStyle="regular"
                      className={`TreeNavigator__item-title ${truncation ? 'truncation' : ''
                        } ${!item.children && !item.hasChildren ? 'TreeNavigator__item-title__bold' : ''
                        }`}
                    />
                  </div>
                </div>
              )}
            </div>

            {!isCollapsed(item) && (
              <div
                style={{
                  marginLeft: hierarchicalSpacing ? `${stepSpacing}px` : '2px',
                  paddingLeft: hierarchicalSpacing
                    ? '0px'
                    : `${childSpacing}px`
                }}
              >
                {loadingNodes.has(item.id) ? (
                  <TreeNodeSkeleton
                    nodePadding={nodePadding}
                    count={3}
                  />
                ) : (
                  item.children && (
                    <TreeNavigator
                      data={item.children}
                      collapsible={collapsible}
                      collapseOnSelectItem={collapseOnSelectItem}
                      selectOnChevronClick={selectOnChevronClick}
                      hoverable={hoverable}
                      hoverScale={hoverScale}
                      hoverItem={hoverItem}
                      checkbox={checkbox}
                      checkboxFocus={checkboxFocus}
                      checkboxLinked={checkboxLinked}
                      collapsedIds={collapsedIds}
                      selectedItem={selectedItem}
                      truncation={truncation}
                      titleVariant={titleVariant}
                      nodePadding={nodePadding}
                      childSpacing={childSpacing}
                      hierarchicalSpacing={hierarchicalSpacing}
                      stepSpacing={stepSpacing}
                      translateOnExpansion={translateOnExpansion}
                      chevronVersion={chevronVersion}
                      chevronIcon={chevronIcon}
                      chevronSize={chevronSize}
                      chevronClassname={chevronClassname}
                      prefixIconSize={prefixIconSize}
                      prefixIconClassName={prefixIconClassName}
                      prefixExpandedIconClassName={prefixExpandedIconClassName}
                      showBullets={showBullets}
                      bulletClassname={bulletClassname}
                      onSelect={onSelect}
                      onCollapse={onCollapse}
                      onDataChange={(updatedChildren) => {
                        if (onDataChange) {
                          const updatedData = updateNodeInTree(
                            data,
                            item.id,
                            (node) => ({ ...node, children: updatedChildren })
                          );
                          const finalData = checkboxLinked ? updateParentStates(updatedData) : updatedData;
                          onDataChange(finalData);
                        }
                      }}
                      onExpand={onExpand}
                    />
                  )
                )}
              </div>
            )}
          </div>
        );
      })}
    </>
  )
}

const TreeNodeSkeleton = ({
  nodePadding,
  count = 3
}: {
  nodePadding: number | null;
  count?: number;
}) => {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className="TreeNavigator__skeleton-item"
          style={{
            padding: `${nodePadding}px`
          }}
        >
          <div className="TreeNavigator__skeleton-text-container">
            <div className="TreeNavigator__skeleton-text" />
          </div>
        </div>
      ))}
    </>
  );
};

const PrefixIcon = ({
  item,
  isCollapsed,
  prefixIconSize,
  prefixIconClassName,
  prefixExpandedIconClassName
}: {
  item: TreeNavigatorData;
  isCollapsed: (item: TreeNavigatorData) => boolean;
  prefixIconSize: IconSize;
  prefixIconClassName: string;
  prefixExpandedIconClassName: string;
}) => {
  const iconClasses = (extraClass: string) => [
    'TreeNavigator__item-icon',
    extraClass || '',
    !item.children ? 'TreeNavigator__item-icon-child' : ''
  ].filter(Boolean).join(' ');

  return (
    <>
      {((item.icon && isCollapsed(item)) || !item.expandedIcon) && (
        typeof item.icon === 'string' ? (
          <Icon
            version="v2"
            icon={item.icon}
            size={typeof prefixIconSize === 'string' ? prefixIconSize : 'original'}
            className={iconClasses(prefixIconClassName)}
          />
        ) : (
          item.icon
        )
      )}

      {(item.expandedIcon && !isCollapsed(item)) && (
        typeof item.expandedIcon === 'string' ? (
          <Icon
            version="v2"
            icon={item.expandedIcon}
            size={typeof prefixIconSize === 'string' ? prefixIconSize : 'original'}
            className={iconClasses(prefixExpandedIconClassName)}
          />
        ) : (
          item.expandedIcon
        )
      )}
    </>
  );
}

export default TreeNavigator